<template>
  <div class="datetime-range-picker" :class="{ 'datetime-range-picker-prepend': $slots.prepend }">
    <slot name="prepend"></slot>
    <!-- <el-popover v-show="showPopover" placement="bottom-start" trigger="hover" :content="formatValue"> -->
    <el-date-picker
      slot="reference"
      style="width: 100%"
      class="datetime-range-picker-input"
      :value="value"
      :type="pickerType"
      :range-separator="rangeSeparatorText"
      :start-placeholder="startPlaceholderText"
      :end-placeholder="endPlaceholderText"
      :format="pickerFormat"
      :value-format="pickerValueFormat"
      :default-time="defaultTime"
      :picker-options="pickerOptions"
      clearable
      v-bind="$attrs"
      @input="onInput"
    />
    <!-- </el-popover> -->
  </div>
</template>

<script>
import { DISPLAY_TIMEZONE, getCalendarPartsInTimeZone } from '@/utils/displayTimezone'

function pad2(n) {
  return n < 10 ? `0${n}` : String(n)
}

function formatYMD({ y, m, d }) {
  return `${y}-${pad2(m)}-${pad2(d)}`
}

function formatYMDHMS({ y, m, d }, h, i, s) {
  return `${formatYMD({ y, m, d })} ${pad2(h)}:${pad2(i)}:${pad2(s)}`
}

function addDaysYMD({ y, m, d }, deltaDays) {
  // 使用 UTC 避免本地时区影响，仅做日期滚动
  const base = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)))
  base.setUTCDate(base.getUTCDate() + deltaDays)
  return { y: base.getUTCFullYear(), m: base.getUTCMonth() + 1, d: base.getUTCDate() }
}

function lastDayOfMonthYMD({ y, m }) {
  const d = new Date(Date.UTC(Number(y), Number(m), 0))
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() }
}

function buildLagosRangeStrings({ startYMD, endYMD, pickerType }) {
  if (pickerType === 'daterange') {
    return [formatYMD(startYMD), formatYMD(endYMD)]
  }
  return [formatYMDHMS(startYMD, 0, 0, 0), formatYMDHMS(endYMD, 23, 59, 59)]
}

function getLagosTodayYMD() {
  const now = new Date()
  const p = getCalendarPartsInTimeZone(now, DISPLAY_TIMEZONE)
  return { y: p.y, m: p.m, d: p.d, a: p.a }
}

function buildShortcuts(pickerType, labels) {
  return [
    {
      text: labels.today,
      onClick(picker) {
        const today = getLagosTodayYMD()
        picker.$emit('pick', buildLagosRangeStrings({ startYMD: today, endYMD: today, pickerType }))
      },
    },
    {
      text: labels.yesterday,
      onClick(picker) {
        const today = getLagosTodayYMD()
        const y = addDaysYMD(today, -1)
        picker.$emit('pick', buildLagosRangeStrings({ startYMD: y, endYMD: y, pickerType }))
      },
    },
    {
      text: labels.thisWeek,
      onClick(picker) {
        const today = getLagosTodayYMD()
        const diffToMonday = today.a === 0 ? 6 : today.a - 1
        const monday = addDaysYMD(today, -diffToMonday)
        const sunday = addDaysYMD(monday, 6)
        picker.$emit('pick', buildLagosRangeStrings({ startYMD: monday, endYMD: sunday, pickerType }))
      },
    },
    {
      text: labels.lastWeek,
      onClick(picker) {
        const today = getLagosTodayYMD()
        const diffToMonday = today.a === 0 ? 6 : today.a - 1
        const thisMonday = addDaysYMD(today, -diffToMonday)
        const lastMonday = addDaysYMD(thisMonday, -7)
        const lastSunday = addDaysYMD(lastMonday, 6)
        picker.$emit('pick', buildLagosRangeStrings({ startYMD: lastMonday, endYMD: lastSunday, pickerType }))
      },
    },
    {
      text: labels.thisMonth,
      onClick(picker) {
        const today = getLagosTodayYMD()
        const first = { y: today.y, m: today.m, d: 1 }
        const last = lastDayOfMonthYMD({ y: today.y, m: today.m })
        picker.$emit('pick', buildLagosRangeStrings({ startYMD: first, endYMD: last, pickerType }))
      },
    },
    {
      text: labels.lastMonth,
      onClick(picker) {
        const today = getLagosTodayYMD()
        const prevMonth = today.m === 1 ? { y: today.y - 1, m: 12 } : { y: today.y, m: today.m - 1 }
        const first = { y: prevMonth.y, m: prevMonth.m, d: 1 }
        const last = lastDayOfMonthYMD(prevMonth)
        picker.$emit('pick', buildLagosRangeStrings({ startYMD: first, endYMD: last, pickerType }))
      },
    },
  ]
}

export default {
  name: 'DateTimeRangePicker',
  inheritAttrs: false,
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    pickerType: {
      type: String,
      default: 'datetimerange',
      validator(v) {
        return ['datetimerange', 'daterange'].indexOf(v) !== -1
      },
    },
    i18n: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    labels() {
      const d = {
        rangeSeparator: '至',
        startPlaceholder: '开始日期',
        endPlaceholder: '结束日期',
        today: '今日',
        yesterday: '昨日',
        thisWeek: '本周',
        lastWeek: '上周',
        thisMonth: '本月',
        lastMonth: '上月',
      }
      return { ...d, ...(this.i18n || {}) }
    },
    rangeSeparatorText() {
      return this.labels.rangeSeparator
    },
    startPlaceholderText() {
      return this.labels.startPlaceholder
    },
    endPlaceholderText() {
      return this.labels.endPlaceholder
    },
    pickerFormat() {
      return this.pickerType === 'daterange' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm:ss'
    },
    pickerValueFormat() {
      return this.pickerType === 'daterange' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm:ss'
    },
    defaultTime() {
      // datetimerange 手动选择日期时，结束时间默认 23:59:59
      return this.pickerType === 'daterange' ? undefined : ['00:00:00', '23:59:59']
    },
    pickerOptions() {
      return {
        shortcuts: buildShortcuts(this.pickerType, this.labels),
      }
    },
    formatValue() {
      const v = Array.isArray(this.value) ? this.value.filter(Boolean) : []
      if (v.length <= 1) return v[0] || ''
      return `${v[0]} ${this.rangeSeparatorText} ${v[1]}`
    },
    showPopover() {
      return this.formatValue.length > 1
    },
  },
  methods: {
    onInput(val) {
      this.$emit('input', val || [])
    },
  },
}
</script>

<style lang="scss" scoped>
.datetime-range-picker {
  display: inline-block;
  width: 100%;
  &.datetime-range-picker-prepend {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    .datetime-range-picker-input {
      flex: 1;
    }

    ::v-deep {
      .el-input input {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-right: none;
        background-color: #f5f7fa;
        color: #909399;
      }
      .el-date-editor {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }
}
</style>
